<script lang="ts">
	import Select, { Option } from '@smui/select'
	import { getAttributeStock } from '$lib/js/products'
	import { page } from '$app/state'
	import { t } from 'svelte-i18n-lingui'
	import { is_set_object } from '$lib/js/helpers'
	import { goto } from '$app/navigation'
	import { onMount } from 'svelte'
	import { storeConfigurableChild } from '$lib/js/stores'
	import type { ConfigurableChild, FullProduct } from '$lib/js/elasticsearch/types'
	import type { ConfigurableOption } from '$lib/js/magento/types'

	interface Props {
		product: FullProduct;
	}

	let { product }: Props = $props();

	let selectOptions: ConfigurableOption[] = $state([])
	let selectedChild: ConfigurableChild | undefined
	let selectedValues: string[] = $state([])

	// Initialize component state
	const parent = page.data.parent
	const isConfigurable = product.type_id === 'configurable' || is_set_object(parent)
	const config_children: ConfigurableChild[] = isConfigurable
		? product.type_id === 'configurable'
			? product.configurable_children
			: parent.configurable_children
		: []

	function initializeSelectOptions() {
		if (!isConfigurable) return

		const sourceProduct = product.type_id === 'configurable' ? product : parent
		selectOptions = (sourceProduct.configurable_options || []).filter((options) => options.values)

		// Add stock information to options
		if (selectOptions.length === 1) {
			const key = selectOptions[0].attribute_code
			selectOptions[0].values = getAttributeStock(config_children, selectOptions[0].values, key)
		}

		// Initialize selected values
		if (is_set_object(parent)) {
			// For child products, set the current product's attribute as selected
			const currentChild = config_children.find((child) => child.id === product.id)
			if (currentChild) {
				selectedValues = selectOptions.map((option) =>
					currentChild[option.attribute_code].toString()
				)
			}
		} else {
			// For parent products, set first available option as default
			selectedValues = selectOptions.map((option) => {
				const firstAvailable = option.values.find((val) => val?.stock > 0)
				const defaultValue = firstAvailable?.value_index ?? option.values[0].value_index
				return defaultValue.toString()
			})
		}

		// Initialize selected child
		updateSelectedChild()
	}

	function updateSelectedChild() {
		if (!config_children?.length || !selectOptions?.length) return

		const matchingChild = config_children.find((child) => {
			return selectOptions.every((option, index) => {
				const childValue = child[option.attribute_code]?.toString()
				return childValue === selectedValues[index]
			})
		})

		if (matchingChild) {
			selectedChild = {
				...matchingChild,
				attributes: selectOptions.map((option, index) => ({
					attribute_id: option.attribute_id,
					attribute_value: selectedValues[index],
				})),
			}
			$storeConfigurableChild = product.type_id === 'configurable' ? selectedChild : undefined
		}
	}

	function handleOptionChange() {
		updateSelectedChild()
		if (selectedChild) {
			goto(selectedChild.url_key)
		}
	}

	// Initialize on mount to ensure all reactive statements are properly handled
	onMount(() => {
		initializeSelectOptions()
	})
</script>

{#if isConfigurable && selectedValues.length > 0}
	{#each selectOptions as selectOption, k}
		<div class="u-select">
			<Select
			onSMUIMenuSelected={handleOptionChange}
				bind:value={selectedValues[k]}
				label={selectOption.label}
			>
				{#each selectOption.values as option}
					<Option value={option.value_index.toString()}>
						<span class="stock-wrap stock-{option?.stock}">
							{option.label}
							{#if option?.stock === 0}
								<span class="no-stock">({$t`Not available`})</span>
							{:else if option?.stock > 0}
								<span class="stock">({$t`Available`})</span>
							{/if}
						</span>
					</Option>
				{/each}
			</Select>
		</div>

		{#if !selectedValues[k]}
			<p class="not-selected">{$t`Please, select an option`}</p>
		{/if}
	{/each}
{/if}

<style lang="scss">
	.u-select {
		margin-top: 10px;
		margin-bottom: 10px;

		span.stock,
		span.no-stock {
			font-size: 12px;
			margin-left: 6px;
		}

		span.stock {
			color: var(--secondary);
		}

		span.no-stock {
			color: var(--red);
		}
	}

	.not-selected {
		color: var(--red);
		margin-top: 10px;
		margin-bottom: 25px;
	}
</style>
